import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'

import { LoadingFull } from '@/components/molecules/loading-full'
import { LanguageSelect } from '@/components/molecules/language-select'
import { Button, ButtonType } from '@/components/atoms/button'
import { BirthDatePicker } from '@/components/organisms/customer/_detail/birth-date-picker'
import { countryList } from '@/libs/nationality'
import { occupationsWithOthers } from '@/libs/occupations'
import * as api from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { InputField } from '@/components/molecules/input-field'
import { Checkbox } from '@/components/atoms/checkbox'

type FormType = {
  name?: string
  nameKana?: string
  birthDate?: string
  gender?: string
  address?: string
  postalCode?: string
  nationality?: string
  countryName?: string
  email?: string
  telephone?: string
  occupation?: string
  otherOccupation?: string
  company?: string
  previousPlaceToStay?: string
  nextPlaceToStay?: string
  accompany: { id?: string; name?: string }[]
}

export const TabletRegisterPage = () => {
  const [isDeliveryConfirm, setIsDeliveryConfirm] = useState<boolean>(true)

  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const checkinId = params.get('checkinId')
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormType>()
  const watchFormData = watch()
  const { fields: accompanyFields, append: addAccompany } = useFieldArray({ control, name: 'accompany' })
  const [fullLoading, setFullLoading] = useState<boolean>(false)
  const [actionLoading, setActionLoading] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [checkin, setCheckin] = useState<any>()
  const [zipFetchLoading, setZipFetchLoading] = useState(false)

  const genders = { M: t('Male'), F: t('Female') }
  const nationality = countryList(i18n.language, false)

  const defaultPreviousPlaceToStay = '自宅'

  const onSubmit = async (formData: FormType) => {
    try {
      setActionLoading(true)
      await api.adminCheckinApprovedTabletRegister({
        checkinId,
        checkin: {
          previousPlaceToStay: formData.previousPlaceToStay,
          nextPlaceToStay: formData.nextPlaceToStay,
          accompany: formData.accompany.filter(a => a.name),
          deleteAccompany: formData.accompany.filter(a => !a.name && a.id),
          guest: {
            name: formData.name,
            nameKana: formData.nameKana,
            birthDate: formData.birthDate,
            nationality: formData.nationality === 'NJP' ? formData.countryName : formData.nationality,
            gender: formData.gender,
            postalCode: formData.postalCode,
            address: formData.address,
            email: formData.email,
            telephone: formData.telephone,
            occupation: formData.occupation,
            otherOccupation: formData.otherOccupation,
            company: formData.company,
            deliveryAgreementStatus: isDeliveryConfirm ? 'deliverable' : 'nonDeliverable',
          },
        },
      })
      await getCheckinData({ withoutLoader: true })

      setSuccess(true)
    } catch (error) {
      errorHandler(error)
    } finally {
      setActionLoading(false)
    }
  }

  const getCheckinData = async ({ withoutLoader = false } = {}) => {
    try {
      if (!withoutLoader) setFullLoading(true)
      const checkin = await api.fetchAdminCheckin(checkinId)
      setCheckin(checkin)
    } catch (error) {
      errorHandler(error)
    } finally {
      setFullLoading(false)
    }
  }

  const setFieldsValuesByCheckin = () => {
    const guest = checkin?.checkin?.guest || {}

    setValue('name', guest.name)
    setValue('nameKana', guest.nameKana)
    setValue('birthDate', guest.birthDate)
    setValue('gender', guest.gender)
    setValue('postalCode', guest.postalCode)
    setValue('address', guest.address)
    if (!guest.nationality || guest.nationality === 'JPN') {
      setValue('nationality', guest.nationality)
    } else {
      setValue('nationality', 'NJP')
      setValue('countryName', guest.nationality)
    }
    setValue('email', guest.email)
    setValue('telephone', guest.telephone)
    setValue('occupation', guest.occupation)
    setValue('otherOccupation', guest.otherOccupation)
    setValue('company', guest.company)

    // 日本語以外の言語設定時は、日本語で保存された値が表示されないよう空欄にする
    setValue(
      'previousPlaceToStay',
      checkin?.checkin?.previousPlaceToStay === defaultPreviousPlaceToStay ? t('Home') : checkin?.checkin?.previousPlaceToStay || '',
    )
    setValue(
      'nextPlaceToStay',
      checkin?.checkin?.nextPlaceToStay === defaultPreviousPlaceToStay ? t('Home') : checkin?.checkin?.nextPlaceToStay || '',
    )

    setValue('accompany', checkin?.checkin?.accompanyList?.length ? checkin?.checkin?.accompanyList : [{ name: undefined }])

    setIsDeliveryConfirm(checkin?.checkin?.deliveryAgreementStatus !== 'nonDeliverable')
  }

  const fetchZipCode = async (value: string) => {
    try {
      if (zipFetchLoading) {
        return
      }
      setZipFetchLoading(true)
      const response = await api.fetchZipCode(value)
      const result = response?.results[0]
      setValue('address', `${result?.address1}${result?.address2}${result?.address3}`)
    } catch {
      // nothing todo
    } finally {
      setZipFetchLoading(false)
    }
  }

  const getNationalityByValue = v => nationality.find(n => n.value === v)?.name || ''

  useEffect(() => {
    const language = localStorage.getItem('lang')

    if (language) i18n.changeLanguage(language)
  }, [])

  useEffect(() => {
    if (!checkinId) history.replace('/')

    getCheckinData()
  }, [checkinId])

  useEffect(() => {
    setFieldsValuesByCheckin()
  }, [checkin, i18n.language])

  useEffect(() => {
    if (success) setShowSuccessModal(true)
  }, [success])

  return (
    <div css={{ maxWidth: 960, margin: 'auto', padding: '56px 32px', position: 'relative', minHeight: '100vh' }}>
      <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 css={{ fontSize: 26, fontWeight: 'bold' }}>{t('Guest Registration Card')}</h1>
        <div>
          <LanguageSelect drawerCss={{ right: 'calc(50% - min(50vw, 480px) + 12px)', top: 98, minWidth: 200 }} />
        </div>
      </div>
      {fullLoading ? (
        <LoadingFull isLoading={true} />
      ) : (
        <div css={{ position: 'relative' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div css={tableContainer}>
              <>
                <div css={[labeldiv, { minHeight: 40 }]}>{i18n.language === 'ja' && t('Furigana')}</div>
                <div css={{ minHeight: 37 }}>
                  {i18n.language === 'ja' && (
                    <input {...register('nameKana')} css={textInput} placeholder="シメイ" className={success ? 'success' : ''} />
                  )}
                </div>
                <div
                  css={[
                    noOnlyOneItem,
                    {
                      gridRow: 'span 2 / span 2',
                      gridTemplateColumns: '3fr 2fr',
                    },
                  ]}
                >
                  <div>
                    {success ? (
                      <div>{watchFormData.birthDate}</div>
                    ) : (
                      <Controller
                        control={control}
                        name={'birthDate'}
                        render={({ field: { onChange, value } }) => (
                          <BirthDatePicker value={value} onChange={onChange} placeholder={t('Date of birth')} />
                        )}
                      />
                    )}
                  </div>
                  <div>
                    {success ? (
                      <div>{genders[watchFormData.gender || ''] || ''}</div>
                    ) : (
                      <select
                        {...register('gender')}
                        css={selectBoxStyle}
                        className={`${success ? 'success' : ''} ${watchFormData.gender ? 'selected' : ''}`}
                      >
                        <option value={''}>{t('Sex')}</option>
                        <option value="M">{t('Male')}</option>
                        <option value="F">{t('Female')}</option>
                      </select>
                    )}
                  </div>
                </div>
              </>
              <>
                <div css={[labeldiv, { minHeight: 47 }]}>{t('Your name')}</div>
                <div>
                  <input
                    {...register('name')}
                    css={textInput}
                    placeholder={t('Last name') + ' ' + t('First name')}
                    className={success ? 'success' : ''}
                  />
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Address')}</div>
                <div css={{ gridColumn: 'span 2' }}>
                  {i18n.language === 'ja' && !success && (
                    <div css={postalCodeBlockStyle}>
                      <Controller
                        name="postalCode"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <InputField
                            type="tel"
                            value={value}
                            name={name}
                            handleChangeData={onChange}
                            label={t('Search for an address by postal code')}
                            placeholder="1234567"
                            fieldWidth="fit-content"
                            width="110px"
                            marginBottom={0}
                          />
                        )}
                      />
                      <Button
                        buttonType={ButtonType.Secondary}
                        width={110}
                        height={32}
                        fontSize={16}
                        fontWeight="normal"
                        loading={zipFetchLoading}
                        onClick={() => {
                          watchFormData.postalCode && fetchZipCode(watchFormData.postalCode)
                        }}
                      >
                        {t('Search by Address')}
                      </Button>
                    </div>
                  )}
                  <input
                    {...register('address')}
                    css={textInput}
                    placeholder={t('Street Address, Apt/Suite (if any), City, State/Province')}
                    className={success ? 'success' : ''}
                  />
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Residence')}</div>
                <div>
                  {success ? (
                    <div>{watchFormData.nationality === 'NJP' ? t('Foreign nationality') : t('Japanese nationality')}</div>
                  ) : (
                    <select
                      {...register('nationality')}
                      css={selectBoxStyle}
                      className={`${success ? 'success' : ''} ${watchFormData.nationality ? 'selected' : ''}`}
                    >
                      <option value={''}>{t('Residence')}</option>
                      <option value={'JPN'}>{t('Japanese nationality')}</option>
                      <option value={'NJP'}>{t('Foreign nationality')}</option>
                    </select>
                  )}
                </div>
                <div>
                  {watchFormData.nationality === 'NJP' && (
                    <>
                      {success ? (
                        <div>{getNationalityByValue(watchFormData.countryName)}</div>
                      ) : (
                        <select
                          {...register('countryName')}
                          css={selectBoxStyle}
                          className={`${success ? 'success' : ''} ${watchFormData.countryName ? 'selected' : ''}`}
                        >
                          <option value={''}>{t('Country name')}</option>
                          {nationality.map((country, index) => {
                            return (
                              <option value={country.value} key={index}>
                                {country.name}
                              </option>
                            )
                          })}
                        </select>
                      )}
                    </>
                  )}
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Contact')}</div>
                <div css={errors.telephone ? { border: '1px solid red', margin: -1 } : {}}>
                  <input
                    {...register('telephone', {
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('Please enter numbers only, without symbols or spaces'),
                      },
                    })}
                    css={[textInput, errors.telephone ? { color: 'red' } : {}]}
                    placeholder={t('Phone number')}
                    className={success ? 'success' : ''}
                  />
                  {!!errors.telephone && (
                    <div css={{ fontSize: 10, color: 'rgba(255,0,0,0.5)', marginTop: 4 }}>{errors.telephone?.message}</div>
                  )}
                </div>
                <div css={errors.email ? { border: '1px solid red', margin: -1 } : {}}>
                  <input
                    {...register('email', {
                      pattern: {
                        value: /^.+@.+\..+$/,
                        message: t('Please enter a valid email address'),
                      },
                    })}
                    css={textInput}
                    placeholder={t('Email address')}
                    className={success ? 'success' : ''}
                  />
                  {!!errors.email && <div css={{ fontSize: 10, color: 'rgba(255,0,0,0.5)', marginTop: 4 }}>{errors.email?.message}</div>}
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Occupation')}</div>
                <div>
                  {success ? (
                    <div>{watchFormData.occupation}</div>
                  ) : (
                    <select
                      {...register('occupation')}
                      css={selectBoxStyle}
                      placeholder={t('Occupation')}
                      className={`${success ? 'success' : ''} ${watchFormData.occupation ? 'selected' : ''}`}
                    >
                      <option value={''}>{t('Occupation')}</option>
                      {occupationsWithOthers.map((occupation, index) => {
                        return (
                          <option value={`${t(occupation.value)}`} key={index}>
                            {t(occupation.label)}
                          </option>
                        )
                      })}
                    </select>
                  )}
                </div>
                <div>
                  {(watchFormData.occupation === t('SelectOptions.Occupation.Employee') ||
                    watchFormData.occupation === 'Employee' ||
                    watchFormData.occupation === '会社員' ||
                    watchFormData.occupation === t('SelectOptions.Occupation.SelfEmployed') ||
                    watchFormData.occupation === 'SelfEmployed' ||
                    watchFormData.occupation === '自営業') && (
                    <>
                      {success ? (
                        <div>{watchFormData.company}</div>
                      ) : (
                        <input
                          {...register('company')}
                          css={textInput}
                          placeholder={t('Company name')}
                          className={success ? 'success' : ''}
                        />
                      )}
                    </>
                  )}
                  {(watchFormData.occupation === t('SelectOptions.Occupation.Other') ||
                    watchFormData.occupation === 'その他' ||
                    watchFormData.occupation === 'Other') && (
                    <>
                      {success ? (
                        <div>{watchFormData.otherOccupation}</div>
                      ) : (
                        <input
                          {...register('otherOccupation')}
                          css={textInput}
                          placeholder={t('SelectOptions.Occupation.Other')}
                          className={success ? 'success' : ''}
                        />
                      )}
                    </>
                  )}
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Place to Stay')}</div>
                <div>
                  <input
                    {...register('previousPlaceToStay')}
                    css={textInput}
                    placeholder={t('Previous Place to Stay')}
                    className={success ? 'success' : ''}
                  />
                </div>
                <div>
                  <input
                    {...register('nextPlaceToStay')}
                    css={textInput}
                    placeholder={t('Next Place to Stay')}
                    className={success ? 'success' : ''}
                  />
                </div>
              </>
              <>
                <div css={labeldiv}>{t('Companion')}</div>
                <div
                  css={[
                    noOnlyOneItem,
                    {
                      position: 'relative',
                      gridColumn: 'span 2',
                      gridTemplateColumns: '1fr',
                    },
                  ]}
                >
                  {accompanyFields.map((field, index) => (
                    <div key={field.id} css={{ height: 60 }}>
                      <input
                        {...register(`accompany.${index}.name`)}
                        css={textInput}
                        placeholder={t('Full name')}
                        className={success ? 'success' : ''}
                      />
                    </div>
                  ))}
                  {!success && (
                    <div
                      onClick={() => addAccompany({ name: undefined })}
                      css={{ position: 'absolute', right: 4, bottom: 14, color: '#f2a40b', cursor: 'pointer' }}
                    >
                      {t('Addition')}
                    </div>
                  )}
                </div>
              </>
            </div>
            <div css={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, position: 'relative' }}>
              <Checkbox
                value={isDeliveryConfirm}
                label={t('Receive campaign information and special offers by e-mail')}
                onChange={setIsDeliveryConfirm}
                style={css({
                  img: { width: 25, height: 25, marginRight: 16 },
                  p: { fontSize: 'initial', fontWeight: 'normal', letterSpacing: 0.9 },
                })}
              />
              {actionLoading ? (
                <div css={{ marginTop: 32 }}>
                  <LoadingFull isLoading={true} />
                </div>
              ) : success ? (
                <Button
                  onClick={() => {
                    localStorage.setItem('checkinUpdated', checkinId ?? '')
                    window.close()
                  }}
                  buttonType={1}
                  width={238}
                  height={48}
                  fontSize={18}
                >
                  {t('Next')}
                </Button>
              ) : (
                <Button buttonType={1} width={238} height={48} fontSize={18} type="submit">
                  {t('Input completed')}
                </Button>
              )}
            </div>
          </form>
          {showSuccessModal && (
            <div
              css={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(64, 64, 64, 0.58)',
              }}
            >
              <div
                css={{
                  backgroundColor: '#fff',
                  borderRadius: 31,
                  position: 'relative',
                  padding: 40,
                  width: '100%',
                  margin: 16,
                  maxWidth: 600,
                }}
              >
                <img
                  src={require('@/static/images/delete_black.svg')}
                  css={{ position: 'absolute', right: 16, top: 16, width: 40, display: 'block', cursor: 'pointer' }}
                  onClick={() => setShowSuccessModal(false)}
                />
                <h1 css={{ fontSize: 24, fontWeight: 'bold', textAlign: 'center', color: '#F2A40B', marginTop: 16, lineHeight: 1.6 }}>
                  {t('Thank you for your input.')}
                  <br />
                  {t('Please keep the screen open and let a staff member know.')}
                </h1>
                <img
                  src={require('@/static/images/tablet-register/acceptance-completed.png')}
                  css={{ margin: '32px auto', maxWidth: '100%', display: 'block', width: 360 }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const tableContainer = css({
  border: '2px solid #676767',
  borderRadius: 12,
  height: '100%',
  marginTop: 16,
  overflow: 'hidden',
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 1fr',
  gap: '1px',
  backgroundColor: '#676767',
  ' > div': {
    backgroundColor: '#fff',
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  '.mbsc-input-box': {
    width: '100%',
    input: {
      '&.mbsc-control': {
        color: '#272727',
        fontWeight: 500,
        height: 40,
        border: '1px solid #676767',
        borderRadius: 8,
        backgroundPosition: '95% center',
        backgroundImage: `url(${require('@/static/images/self-checkin-payment/arrow.svg')})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'initial',
        fontSize: 16,
        '::placeholder': {
          color: '#ccc',
        },
        ':focus': {
          border: '1px solid #676767',
        },
      },
    },
  },
  '.SingleDatePicker_picker__directionLeft': {
    top: 'initial !important',
  },
  '.SingleDatePickerInput_calendarIcon': {
    display: 'none',
  },
})

const labeldiv = css({
  fontWeight: 'bold',
  minHeight: 60,
})

const textInput = css({
  border: 'none',
  backgroundColor: 'none',
  width: '100%',
  color: '#272727',
  '::placeholder': {
    color: '#ccc',
  },
  '&.success': {
    pointerEvents: 'none',
    '::placeholder': {
      color: '#fff',
    },
  },
})

const selectBoxStyle = css({
  width: '100%',
  height: 40,
  border: '1px solid #676767',
  borderRadius: 8,
  appearance: 'none',
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  backgroundColor: '#fff',
  backgroundPosition: '95% center',
  backgroundImage: `url(${require('@/static/images/self-checkin-payment/arrow.svg')})`,
  backgroundRepeat: 'no-repeat',
  color: '#ccc',
  '&.selected': {
    color: '#272727',
  },
})

const noOnlyOneItem = css({
  display: 'grid !important',
  gap: 1,
  padding: '0 !important',
  backgroundColor: '#676767 !important',
  alignItems: 'initial !important',
  '> div': { backgroundColor: '#fff', display: 'flex', alignItems: 'center', padding: '8px' },
})

const postalCodeBlockStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 4,
  border: '1px dashed #cccccc',
  borderRadius: 5,
  backgroundColor: '#f2f2f2',
  marginBottom: 8,
  '>div': {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    p: { marginBottom: 0 },
  },
  input: { fontSize: 16 },
})
